import { Link, useParams } from "react-router-dom";
import type {
  AgentSummary,
  CronJobView,
  RunSummary,
} from "@agentdash/shared";
import type {
  GhCommit,
  GhPullRequest,
  GhWorkflowRun,
} from "../../lib/api-client.js";
import { useRepo } from "./hooks.js";
import { originToWebUrl } from "../../lib/repo-link.js";
import { CronRow } from "./CronRow.js";
import { SpecPanel } from "./SpecPanel.js";
import {
  composeStatusColor,
  composeStatusIcon,
  conclusionColor,
  conclusionIcon,
  statusPillStyle,
} from "./statusIcons.js";
import { StatusDot } from "../../components/StatusDot.js";
import { Timestamp } from "../../components/Timestamp.js";
import { formatDurationMs, formatRelative } from "../../lib/time.js";

export function RepoDetail() {
  const { owner, name } = useParams<{ owner: string; name: string }>();
  const slug = owner && name ? `${owner}/${name}` : "";
  const q = useRepo(slug);

  if (!slug) return <div style={{ padding: 16 }}>Missing slug.</div>;
  if (q.isLoading) {
    return <div style={{ padding: 24, color: "var(--muted)" }}>Loading…</div>;
  }
  if (q.isError) {
    return (
      <div style={{ padding: 24, color: "var(--err)" }}>
        Failed: {String((q.error as Error).message ?? q.error)}
      </div>
    );
  }
  const repo = q.data;
  if (!repo) return null;

  return (
    <>
      <section className="panel">
        <div
          className="panel-header"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <span>{repo.slug}</span>
          <a
            href={originToWebUrl(repo.origin_url)}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12, color: "var(--muted)" }}
          >
            github ↗
          </a>
        </div>
        <div
          style={{
            padding: 16,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16,
          }}
        >
          <Stat label="default branch" value={repo.default_branch ?? "—"} />
          <Stat label="cron jobs" value={String(repo.cron_job_count)} />
          <Stat label="paused" value={String(repo.paused_count)} />
          <Stat label="wrapped" value={String(repo.wrapped_count)} />
          <Stat
            label="last run"
            value={
              repo.last_run_status ? (
                <>
                  <StatusDot status={repo.last_run_status} />{" "}
                  <Timestamp iso={repo.last_run_at ?? ""} />
                </>
              ) : (
                "—"
              )
            }
          />
          {repo.github.enabled && (
            <Stat
              label="CI"
              value={
                repo.github.latest_workflow_run ? (
                  <a
                    href={repo.github.latest_workflow_run.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: "var(--text)",
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                    title={`${repo.github.latest_workflow_run.conclusion} · ${formatRelative(repo.github.latest_workflow_run.updated_at)}`}
                  >
                    <span
                      style={statusPillStyle(
                        conclusionColor(
                          repo.github.latest_workflow_run.conclusion,
                        ),
                      )}
                    >
                      {conclusionIcon(
                        repo.github.latest_workflow_run.conclusion,
                      )}
                    </span>
                    {repo.github.latest_workflow_run.conclusion}
                  </a>
                ) : (
                  "—"
                )
              }
            />
          )}
          {repo.dokploy.enabled && (
            <Stat
              label="deploy"
              value={
                repo.dokploy.compose ? (
                  <span
                    title={`Dokploy: ${repo.dokploy.compose.status}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span
                      style={statusPillStyle(
                        composeStatusColor(repo.dokploy.compose.status),
                      )}
                    >
                      {composeStatusIcon(repo.dokploy.compose.status)}
                    </span>
                    {repo.dokploy.compose.status}
                  </span>
                ) : (
                  "—"
                )
              }
            />
          )}
        </div>
      </section>

      <CronJobsPanel jobs={repo.cron_jobs} />

      <AgentsPanel
        agents={repo.agents.filter((a) =>
          repo.cron_jobs.some((j) => j.agent_id === a.agent_id),
        )}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))",
          gap: 0,
        }}
      >
        <RecentRunsPanel
          runs={repo.recent_runs}
          wrappedJobsExist={repo.wrapped_count > 0}
        />
        {repo.github.enabled && (
          <>
            <WorkflowPanel run={repo.github.latest_workflow_run} />
            <CommitsPanel
              commits={repo.github.commits}
              error={repo.github.commits_error}
            />
            <PullsPanel prs={repo.github.open_prs} />
          </>
        )}
        {repo.dokploy.enabled && <DokployPanel compose={repo.dokploy.compose} />}
      </div>
      <SpecPanel owner={owner!} name={name!} />
    </>
  );
}

function AgentsPanel({ agents }: { agents: AgentSummary[] }) {
  return (
    <section className="panel">
      <div className="panel-header">Agents ({agents.length})</div>
      {agents.length === 0 ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>
          No host-agent owns a cron job for this repo.
        </div>
      ) : (
        <table className="mobile-cards">
          <tbody>
            {agents.map((a) => (
              <tr key={a.agent_id}>
                <td className="mc-status" style={{ width: 8 }}>
                  <span
                    title={
                      a.run_monitored_path
                        ? `run-monitored at ${a.run_monitored_path}`
                        : "run-monitored not installed — wrap will fail"
                    }
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      background: a.run_monitored_path
                        ? "var(--accent)"
                        : "var(--warn)",
                    }}
                  />
                </td>
                <td className="mc-title">
                  <b>{a.agent_id}</b>{" "}
                  <span style={{ color: "var(--muted)", fontSize: 12 }}>
                    {a.hostname}
                  </span>
                </td>
                <td data-label="Last seen" style={{ color: "var(--muted)", fontSize: 12 }}>
                  {a.last_seen_at
                    ? `seen ${formatRelative(a.last_seen_at)}`
                    : "never seen"}
                </td>
                <td data-label="run-monitored" style={{ color: "var(--muted)", fontSize: 12 }}>
                  {a.run_monitored_path ? "✓" : "✗"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          color: "var(--muted)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: 14, fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

function TruncationFooter({ shown, total }: { shown: number; total: number }) {
  return (
    <div
      style={{
        padding: "8px 16px",
        borderTop: "1px solid var(--border)",
        color: "var(--muted)",
        fontSize: 12,
      }}
    >
      Showing {shown} of {total}
    </div>
  );
}

function CronJobsPanel({ jobs }: { jobs: CronJobView[] }) {
  return (
    <section className="panel">
      <div className="panel-header">Cron jobs ({jobs.length})</div>
      {jobs.length === 0 ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>
          No cron jobs reported for this repo.
        </div>
      ) : (
        jobs.map((j) => <CronRow key={j.cron_job_id} job={j} />)
      )}
    </section>
  );
}

function RecentRunsPanel({
  runs,
  wrappedJobsExist,
}: {
  runs: RunSummary[];
  wrappedJobsExist: boolean;
}) {
  const cap = 12;
  return (
    <section className="panel">
      <div className="panel-header">Recent runs</div>
      {runs.length === 0 ? (
        wrappedJobsExist ? (
          <div style={{ padding: 16, color: "var(--muted)", fontSize: 13 }}>
            No runs are tagged with this repo yet. Wrapped lines created
            before the env-injection rollout don't carry{" "}
            <code>AGENT_REPO</code> — click <b>Wrap</b> on each row once
            more to re-emit the line with attribution, then the next
            scheduled fire (or Run now) will land here.
          </div>
        ) : (
          <div style={{ padding: 16, color: "var(--muted)" }}>
            No wrapped runs yet. Wrap a cron line with{" "}
            <code>run-monitored</code> to start collecting history.
          </div>
        )
      ) : (
        <table className="mobile-cards">
          <tbody>
            {runs.slice(0, cap).map((r) => (
              <tr key={r.trace_id}>
                <td className="mc-status" style={{ width: 8 }}>
                  <StatusDot status={r.status} />
                </td>
                <td className="mc-title">
                  <Link to={`/runs/${r.trace_id}`}>{r.name}</Link>
                </td>
                <td data-label="Started" style={{ color: "var(--muted)" }}>
                  <Timestamp iso={r.started_at} />
                </td>
                <td data-label="Duration" style={{ color: "var(--muted)" }}>
                  {formatDurationMs(r.duration_ms)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {runs.length > cap && <TruncationFooter shown={cap} total={runs.length} />}
    </section>
  );
}

function WorkflowPanel({ run }: { run: GhWorkflowRun | null }) {
  return (
    <section className="panel">
      <div className="panel-header">Latest GitHub Actions run</div>
      {!run ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>
          No workflow runs found on the default branch.
        </div>
      ) : (
        <div style={{ padding: 16, display: "grid", gap: 6 }}>
          <div>
            <span style={{ marginRight: 8 }}>
              {conclusionIcon(run.conclusion)}
            </span>
            <a href={run.url} target="_blank" rel="noreferrer">
              {run.name}
            </a>
          </div>
          <div style={{ color: "var(--muted)", fontSize: 12 }}>
            {run.conclusion} · <Timestamp iso={run.updated_at} /> · sha{" "}
            <code>{run.head_sha.slice(0, 7)}</code>
          </div>
        </div>
      )}
    </section>
  );
}

function CommitsPanel({
  commits,
  error,
}: {
  commits: GhCommit[];
  error: string | null;
}) {
  const cap = 10;
  return (
    <section className="panel">
      <div className="panel-header">
        Recent commits ({commits.length})
      </div>
      {commits.length === 0 ? (
        error ? (
          <div style={{ padding: 16, color: "var(--warn)", fontSize: 13 }}>
            {error}
          </div>
        ) : (
          <div style={{ padding: 16, color: "var(--muted)" }}>—</div>
        )
      ) : (
        <div style={{ padding: 16, display: "grid", gap: 8, fontSize: 13 }}>
          {commits.slice(0, cap).map((c) => (
            <div key={c.sha}>
              <a
                href={c.url}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--accent)" }}
              >
                {c.short_sha}
              </a>{" "}
              {c.message}
              <div style={{ color: "var(--muted)", fontSize: 11 }}>
                {c.author ?? "(unknown)"} · <Timestamp iso={c.date} />
              </div>
            </div>
          ))}
        </div>
      )}
      {commits.length > cap && (
        <TruncationFooter shown={cap} total={commits.length} />
      )}
    </section>
  );
}

function PullsPanel({ prs }: { prs: GhPullRequest[] }) {
  return (
    <section className="panel">
      <div className="panel-header">Open PRs ({prs.length})</div>
      {prs.length === 0 ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>—</div>
      ) : (
        <div style={{ padding: 16, display: "grid", gap: 8, fontSize: 13 }}>
          {prs.map((p) => (
            <div key={p.number}>
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--accent)" }}
              >
                #{p.number}
              </a>{" "}
              {p.title}
              {p.draft && (
                <span
                  style={{
                    marginLeft: 6,
                    color: "var(--muted)",
                    fontSize: 11,
                    padding: "1px 4px",
                    border: "1px solid var(--border)",
                    borderRadius: 3,
                  }}
                >
                  draft
                </span>
              )}
              <div style={{ color: "var(--muted)", fontSize: 11 }}>
                {p.author} · <Timestamp iso={p.updated_at} />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DokployPanel({
  compose,
}: {
  compose: { composeId: string; name: string; status: string } | null;
}) {
  return (
    <section className="panel">
      <div className="panel-header">Dokploy deploy</div>
      {!compose ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>
          No Dokploy compose tracks this repo.
        </div>
      ) : (
        <div style={{ padding: 16, fontSize: 13 }}>
          <div>
            <span style={{ marginRight: 6 }}>
              {composeStatusIcon(compose.status)}
            </span>
            {compose.name}
          </div>
          <div style={{ color: "var(--muted)", fontSize: 11, marginTop: 4 }}>
            status: <b>{compose.status}</b>
          </div>
        </div>
      )}
    </section>
  );
}

